import React from "react";
import { Link } from "react-router-dom";
import LegalPageLayout from "./LegalPageLayout";
import C from "./legal.config";

/**
 * /legal/privacy — Privacy Policy.
 *
 * Structured to cover PIPEDA (Canada) + GDPR-equivalent transparency
 * for EU/UK visitors. Final wording requires Canadian lawyer review.
 */
export default function PrivacyPolicy() {
  return (
    <LegalPageLayout title="Privacy Policy" version={C.versions.privacy}>
      <p>
        This policy describes how <strong>{C.legal_entity_name}</strong>
        ({C.site_url}) collects, uses, discloses, and safeguards your
        personal information. It is written to comply with Canada's
        federal Personal Information Protection and Electronic Documents
        Act (PIPEDA) and applicable provincial privacy law. We provide
        GDPR-equivalent transparency for visitors located in the
        European Economic Area, the United Kingdom, and Switzerland.
      </p>

      <h2 className="legal-h2">1. Who we are</h2>
      <p>
        <strong>{C.legal_entity_name}</strong>, based in {C.province},
        {C.country}, operates LEAGUE HUB. You can reach our privacy
        contact at <a href={`mailto:${C.privacy_email}`}>{C.privacy_email}</a>.
        Our registered business address is {C.legal_address}.
      </p>

      <h2 className="legal-h2">2. What we collect</h2>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Account &amp; OAuth data</strong> — when you sign in with Google or Discord we receive your email, display name, avatar URL, and a stable account identifier.</li>
        <li><strong>Profile content</strong> — anything you add to your profile (display name, banner, game tags, social links).</li>
        <li><strong>Direct messages &amp; uploads</strong> — messages you send, screenshots, clips, team logos, and any other user-generated content.</li>
        <li><strong>Competitive data</strong> — match results, MMR/ELO, badges, leaderboards, league memberships, applications.</li>
        <li><strong>Payments</strong> — Stripe customer ID, subscription status, expiry. We do <em>not</em> store full card numbers; Stripe handles those directly.</li>
        <li><strong>AI prompts</strong> — the text prompt you enter for AI avatar / banner generation, sent to {C.processors.find((p) => p.name.includes("Gemini"))?.name || "our AI provider"} for processing.</li>
        <li><strong>Technical logs</strong> — IP address, user-agent, request paths, error traces — used for security and debugging.</li>
      </ul>

      <h2 className="legal-h2">3. Why we collect it &amp; legal bases</h2>
      <p>
        We process this information to provide the service you requested
        (contract), to operate the platform safely (legitimate interest),
        and where you explicitly opt in — e.g. cookie consent, marketing
        emails — on the basis of <em>consent</em> (PIPEDA
        meaningful consent; GDPR Art. 6(1)(a)).
      </p>

      <h2 className="legal-h2">4. Who we share with</h2>
      <p>
        We use the following service providers ("third-party processors")
        to operate LEAGUE HUB. Each processes data only on our
        instructions and is bound by contractual confidentiality.
      </p>
      <table className="text-[12px] w-full mt-2 border-collapse">
        <thead>
          <tr className="text-left text-white/50">
            <th className="py-1 pr-3">Provider</th>
            <th className="py-1 pr-3">Purpose</th>
            <th className="py-1">Jurisdiction</th>
          </tr>
        </thead>
        <tbody>
          {C.processors.map((p) => (
            <tr key={p.name} className="border-t border-white/10">
              <td className="py-1 pr-3">{p.name}</td>
              <td className="py-1 pr-3">{p.purpose}</td>
              <td className="py-1">{p.jurisdiction}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 className="legal-h2">5. Cross-border transfers</h2>
      <p>
        Most of our processors are based in the United States. Personal
        information transferred outside Canada is protected by
        contractual safeguards (e.g. Stripe DPA, Google Cloud DPA,
        standard contractual clauses where applicable). PIPEDA requires
        us to inform you of this transfer; by using LEAGUE HUB you
        consent to it.
      </p>

      <h2 className="legal-h2">6. Retention</h2>
      <p>
        Account data is retained while your account is active. Payment
        records are retained for the period required by Canadian tax
        law (currently 6 years). DMs and uploaded content are retained
        until you delete them or your account is closed, at which point
        they are removed within 30 days except where retention is
        required by law or to resolve disputes.
      </p>

      <h2 className="legal-h2">7. Your rights</h2>
      <p>You have the right to:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Access</strong> — request a copy of the personal information we hold about you.</li>
        <li><strong>Correction</strong> — update inaccurate information directly in your profile, or contact us.</li>
        <li><strong>Deletion</strong> — request that we delete your account and associated personal data, subject to legal-hold exceptions.</li>
        <li><strong>Portability</strong> — request your data in a machine-readable format.</li>
        <li><strong>Objection / withdraw consent</strong> — opt out of cookies, marketing, and processing based on consent at any time.</li>
      </ul>
      <p>
        To exercise any of these rights, email{" "}
        <a href={`mailto:${C.privacy_email}`}>{C.privacy_email}</a>. We
        respond within 30 days.
      </p>

      <h2 className="legal-h2">8. Children</h2>
      <p>
        LEAGUE HUB is not intended for users under {C.minimum_age}. We
        do not knowingly collect information from anyone under
        {" "}{C.minimum_age}. If we learn we hold such information,
        we delete it. Parents or guardians may contact{" "}
        <a href={`mailto:${C.privacy_email}`}>{C.privacy_email}</a>.
      </p>

      <h2 className="legal-h2">9. Security &amp; breach notification</h2>
      <p>
        We use industry-standard safeguards (TLS in transit, encryption
        at rest, restricted-access database, audit logging). If we
        become aware of a privacy breach that creates a real risk of
        significant harm we will notify affected users and the Office
        of the Privacy Commissioner of Canada (OPC) without
        unreasonable delay, as required by PIPEDA's Breach of Security
        Safeguards Regulations.
      </p>

      <h2 className="legal-h2">10. Marketing &amp; CASL</h2>
      <p>
        We send commercial electronic messages only with your express
        consent under Canada's Anti-Spam Legislation (CASL). Every
        marketing email includes an unsubscribe link. Notifications
        related to your account (security, payments, league moderation)
        are transactional and may be sent without separate consent.
      </p>

      <h2 className="legal-h2">11. Complaints</h2>
      <p>
        If you believe we have mishandled your personal information,
        contact us first at <a href={`mailto:${C.privacy_email}`}>{C.privacy_email}</a>.
        You may also file a complaint with:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>The Office of the Privacy Commissioner of Canada — <a href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer">priv.gc.ca</a>.</li>
        <li>The applicable provincial privacy commissioner (where {C.province} has one).</li>
        <li>If you are in the EU/UK/Switzerland, your local supervisory authority (e.g. CNIL, ICO, FDPIC).</li>
      </ul>

      <h2 className="legal-h2">12. Changes to this policy</h2>
      <p>
        We may update this policy from time to time. The version number
        and "Last updated" date at the top reflect the current revision.
        Material changes will be announced via in-app banner and, where
        we have your consent, via email.
      </p>

      <p className="text-white/45 text-xs">
        See also: <Link to="/legal/cookies" className="underline">Cookies &amp; Tracking</Link> ·{" "}
        <Link to="/legal/terms" className="underline">Terms of Service</Link>.
      </p>
    </LegalPageLayout>
  );
}
